import { CommonModule, isPlatformBrowser } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnDestroy, OnInit, PLATFORM_ID, ViewEncapsulation } from '@angular/core';
import { RouterModule } from '@angular/router';
import { MerchantService } from '@core/merchant/merchant.service';
import { Merchant, MerchantColors } from '@core/merchant/merchant.types';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { TranslocoService } from '@jsverse/transloco';
import { isEqual } from 'lodash';
import { combineLatest, distinctUntilChanged, Subject, takeUntil } from 'rxjs';


/**
 * The cookie-consent component of the merchant website.
 * This is a free INCOMPLETE alternative to Axeptio.
 * However, keep in mind that a true CMP (consent management platform) is made of 2 main components:
 *  - cookie banner (client side) - this component
 *  - platform to store/manage consent records (user's preferences)
 * Therefore, this plugin is not a CMP.
 * To be a valid, registered CMP, you also have to pay about €1.500 every year. The IAB-tcf framework can only be implemented if the solution is a valid CMP.
 * A CMP + IAB-TCF compliant solution is expensive to build and maintain, and this plugin will very likely never become one.
 * I would also assume that google's compliant/certified solutions will not be free.
 */
@Component({
    selector: 'cookie-consent-v3',
    templateUrl: './cookie.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        CommonModule,
        RouterModule,
        FontAwesomeModule
    ],
})
export class CookieConsentComponent implements OnInit, OnDestroy {

    /**
     * The merchant object.
     */
    merchant: Merchant | null = null;

    /**
     * The active language.
     */
    activeLang: string;

    /**
     * The template variables object.
     */
    tpl: {
        isBrowser: boolean;
        merchantColors: MerchantColors;
    } = {
            isBrowser: false,
            merchantColors: null,
        };

    /**
     * Subject for unsubscribing from observables.
     */
    private _unsubscribeAll: Subject<any> = new Subject<any>();
    
    /**
     * The transloco read key for translations.
     */
    private _translocoRead: string = 'application.cookie-consent';

    /**
     * Constructor
     * 
     * @param _platformId - The platform ID.
     * @param _merchantService The merchant service.
     * @param _translocoService The transloco service.
     * @param _changeDetectorRef - The change detector reference.
     */
    constructor(
        @Inject(PLATFORM_ID) private _platformId: any,
        private _merchantService: MerchantService,
        private _translocoService: TranslocoService,
        private _changeDetectorRef: ChangeDetectorRef
    ) {
        this.tpl.isBrowser = isPlatformBrowser(this._platformId);
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * Initializes the component.
     * @returns void
     */
    ngOnInit(): void {
        combineLatest([
            this._merchantService.merchant$,
            this._translocoService.langChanges$
        ])
            .pipe(
                takeUntil(this._unsubscribeAll),
                distinctUntilChanged((previous: any, current: any) =>
                    isEqual(previous, current)
                )
            )
            .subscribe(([merchant, activeLang]: [Merchant, string]) => {
                this.merchant = merchant;
                // Set the template variables
                this._setTemplateVariables();
                // Load the cookie consent if the language has changed
                console.log('Active language changed to', activeLang);
                if (this.activeLang && this.activeLang !== activeLang) {
                    console.log('Loading Cookie Consent');
                    this._loadCookieConsent();
                }
                this.activeLang = activeLang;
                // Mark for check
                this._changeDetectorRef.markForCheck();
                }
            );
    }

    /**
     * Initializes the component's view.
     * If the platform is the browser, it loads the cookie consent.
     */
    ngAfterViewInit(): void {
        if (isPlatformBrowser(this._platformId)) {
            this._retryCookieConsent(1);
        }
    }

    /**
     * Lifecycle hook that is called when the component is destroyed.
     * It unsubscribes all subscriptions to prevent memory leaks.
     */
    ngOnDestroy(): void {
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Sets the template variables for the footer component.
     * @returns void
     */
    private _setTemplateVariables(): void {
        if (!this.merchant) {
            return;
        }
        this.tpl.merchantColors = this._merchantService.getColors(this.merchant);
        // Mark for check
        this._changeDetectorRef.markForCheck();
    }

    private _loadCookieConsent(): void {
        window['CookieConsent'].run({
            onFirstConsent: ({ cookie }) => {
                console.log('onFirstConsent fired', cookie);
            },

            onConsent: ({ cookie }) => {
                console.log('onConsent fired!', cookie);
            },

            onChange: ({ changedCategories, changedServices }) => {
                console.log('onChange fired!', changedCategories, changedServices);
            },

            onModalReady: ({ modalName }) => {
                console.log('ready:', modalName);
            },

            onModalShow: ({ modalName }) => {
                console.log('visible:', modalName);
                if (modalName === 'preferencesModal') {
                    window['CookieConsent'].hide()
                }
            },

            onModalHide: ({ modalName }) => {
                console.log('hidden:', modalName);
            },
            
            guiOptions: {
                consentModal: {
                    layout: 'box',
                    position: 'bottom right',
                    equalWeightButtons: true,
                    flipButtons: false
                },
                preferencesModal: {
                    layout: 'box',
                    position: 'right',
                    equalWeightButtons: true,
                    flipButtons: false
                }
            },
            categories: {
                necessary: {
                    enabled: true, // this category is enabled by default
                    readOnly: true, // this category cannot be disabled
                },
                analytics: {
                    autoClear: {
                        cookies: [
                            {
                                name: /^_ga/, // regex: match all cookies starting with '_ga'
                            },
                            {
                                name: '_gid', // string: exact cookie name
                            },
                        ],
                    },
                    services: {
                        ga: {
                            label: this._translocoService.translate(`${this._translocoRead}.preferences-modal.section-analytics.service-ga`),
                            onAccept: () => { },
                            onReject: () => { },
                        },
                        google_analytics_storage: {
                            label: this._translocoService.translate(`${this._translocoRead}.preferences-modal.section-analytics.service-ga-storage`),
                            onAccept: () => { },
                            onReject: () => { },
                        },
                    },
                },
                marketing: {
                    services: {
                        google_ad_storage: {
                            label: this._translocoService.translate(`${this._translocoRead}.preferences-modal.section-advertisement.service-google-ad-storage`),
                            onAccept: () => { },
                            onReject: () => { },
                        },
                        google_ad_user_data: {
                            label: this._translocoService.translate(`${this._translocoRead}.preferences-modal.section-advertisement.service-google-ad-user-data`),
                            onAccept: () => { },
                            onReject: () => { },
                        },
                        google_ad_personalization: {
                            label: this._translocoService.translate(`${this._translocoRead}.preferences-modal.section-advertisement.service-google-ad-personalization`),
                            onAccept: () => { },
                            onReject: () => { },
                        },
                    },
                }
            },
            language: {
                default: 'custom',
                translations: {
                    custom: {
                        consentModal: {
                            title: this._translocoService.translate(`${this._translocoRead}.consent-modal.title`),
                            description: this._translocoService.translate(`${this._translocoRead}.consent-modal.description`),
                            acceptAllBtn: this._translocoService.translate(`${this._translocoRead}.consent-modal.btn-accept-all`),
                            acceptNecessaryBtn: this._translocoService.translate(`${this._translocoRead}.consent-modal.btn-accept-necessary`),
                            showPreferencesBtn: this._translocoService.translate(`${this._translocoRead}.consent-modal.btn-show-preferences`),
                            footer: this._translocoService.translate(`${this._translocoRead}.consent-modal.footer`)
                        },
                        preferencesModal: {
                            title: this._translocoService.translate(`${this._translocoRead}.preferences-modal.title`),
                            acceptAllBtn: this._translocoService.translate(`${this._translocoRead}.preferences-modal.btn-accept-all`),
                            acceptNecessaryBtn: this._translocoService.translate(`${this._translocoRead}.preferences-modal.btn-accept-necessary`),
                            savePreferencesBtn: this._translocoService.translate(`${this._translocoRead}.preferences-modal.btn-save-preferences`),
                            closeIconLabel: this._translocoService.translate(`${this._translocoRead}.preferences-modal.label-close-icon`),
                            serviceCounterLabel: this._translocoService.translate(`${this._translocoRead}.preferences-modal.label-service-counter`),
                            sections: [
                                {
                                    title: this._translocoService.translate(`${this._translocoRead}.preferences-modal.section-header.title`),
                                    description: this._translocoService.translate(`${this._translocoRead}.preferences-modal.section-header.description`)
                                },
                                {
                                    title: this._translocoService.translate(`${this._translocoRead}.preferences-modal.section-analytics.title`),
                                    description: this._translocoService.translate(`${this._translocoRead}.preferences-modal.section-analytics.description`),
                                    linkedCategory: "analytics"
                                },
                                {
                                    title: this._translocoService.translate(`${this._translocoRead}.preferences-modal.section-advertisement.title`),
                                    description: this._translocoService.translate(`${this._translocoRead}.preferences-modal.section-advertisement.description`),
                                    linkedCategory: "marketing"
                                }
                            ]
                        }
                    }
                }
            }
        });
    }

    /**
     * Retries loading the cookie consent with exponential backoff
     * @param attempt The current attempt number (1-based)
     */
    private _retryCookieConsent(attempt: number): void {
        if (attempt > 5) {
            console.error('Failed to load CookieConsent after 5 attempts');
            return;
        }
        setTimeout(() => {
            console.log(`Retry attempt ${attempt} to load CookieConsent`);
            if (window['CookieConsent']) {
                this._loadCookieConsent();
            } else {
                this._retryCookieConsent(attempt + 1);
            }
        }, 5000);
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

}
